// 通信设置 → 顶层开关之一：业主深夜免打扰（2026-11-05 需求）。
// 单独成组件的原因：① 便于不拉整页 overview 做组件测试；② 通信设置页已经很长，卡片各自独立更好维护。
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { toast } from "../ui/sonner";
import { Loader2, Save, Moon } from "lucide-react";

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1";

/**
 * 需求（2026-11-05，老板）：「业主深夜免打扰」要能在后台勾选，不能只靠改环境变量重启。
 * 老板确认过的口径：「**不勾选就是正常发送**」——
 *   · 勾选   → 发给业主 / 客户的短信在其当地时间 21:00–08:00 不立即发，写进发件箱顺延到当地早上补发；
 *   · 不勾选 → 任何时段都立即发送（与改造前一致，零风险回退）；
 *   · 内部员工、分包商 / 供应商始终照常发送（豁免名单不在本卡上，交由后端固定）。
 * 其它要点：
 *   - 非 admin / ceo 只读（canEdit=false 时控件禁用并提示）；后端 PUT 同样会 403，不只靠前端。
 *   - `lockedByEnv`：运维用 SMS_QUIET_HOURS=off 硬关时页面不可反超（与「自动发送」受
 *     DISABLE_SCHEDULER 锁定的写法一致），保存按钮不出现，避免“点了没反应”。
 *   - `active` 为 false 但已勾选时（本环境没有补发定时任务）必须明说“暂不生效”，
 *     否则管理员会以为已经保护到业主。
 *   - 说明文案里的 21:00–08:00 是后端默认值；下方状态行显示的是**当前实际**时段，
 *     管理员改过时段后以状态行为准。
 * testid 契约：容器 comms-quiet-hours，勾选框 comms-quiet-hours-toggle，
 * 起止时段 comms-quiet-hours-start / -end，保存 comms-quiet-hours-save，状态行 comms-quiet-hours-status。
 */
export default function CommsQuietHoursCard({ quiet, api, zh, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // 以服务端回显为准：保存成功后父层重新拉 overview，这里跟着刷新草稿。
    if (!quiet) { setDraft(null); return; }
    setDraft({ enabled: !!quiet.enabled, startHour: Number(quiet.startHour), endHour: Number(quiet.endHour) });
  }, [quiet?.enabled, quiet?.startHour, quiet?.endHour]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!quiet || !draft) return null;

  const canEdit = !!quiet.canEdit && !quiet.lockedByEnv;
  const dirty = draft.enabled !== !!quiet.enabled
    || draft.startHour !== Number(quiet.startHour)
    || draft.endHour !== Number(quiet.endHour);
  const hours = (h) => `${String(h).padStart(2, "0")}:00`;
  const ALL_HOURS = Array.from({ length: 24 }, (_, h) => h);

  const save = async () => {
    if (!canEdit || busy || !dirty) return;
    setBusy(true);
    try {
      await api.put("/admin/comms/quiet-hours", draft);
      toast.success(draft.enabled
        ? (zh ? "已开启业主深夜免打扰" : "Owner quiet hours on")
        : (zh ? "已关闭业主深夜免打扰：短信立即发送" : "Owner quiet hours off: sends immediately"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.detailText || e?.response?.data?.detail || (zh ? "保存失败" : "Failed"));
    }
    setBusy(false);
  };

  const status = quiet.lockedByEnv
    ? (zh ? "已被环境变量 SMS_QUIET_HOURS=off 锁定关闭，页面无法修改" : "Locked off by env var SMS_QUIET_HOURS=off")
    : !quiet.enabled
      ? (zh ? "当前未勾选：发给业主 / 客户的短信立即发送" : "Unchecked: SMS to owners/clients sends immediately")
      : !quiet.active
        ? (zh ? "已勾选，但当前环境未运行补发任务（DISABLE_SCHEDULER），暂不生效" : "Checked, but no flush scheduler in this environment: not active yet")
        : (zh ? `当前生效：业主 / 客户当地时间 ${hours(quiet.startHour)}–${hours(quiet.endHour)} 的短信顺延到 ${hours(quiet.endHour)} 补发`
          : `Active: quiet ${hours(quiet.startHour)}–${hours(quiet.endHour)} local, delivered at ${hours(quiet.endHour)}`);

  return (
    <div data-testid="comms-quiet-hours" data-enabled={quiet.enabled ? "1" : "0"} data-active={quiet.active ? "1" : "0"}
      className={`rounded-lg border px-4 py-3 ${quiet.active ? "border-slate-200 bg-white" : "border-amber-300 bg-amber-50"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${quiet.active ? "bg-indigo-50 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
          <Moon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">{zh ? "业主深夜免打扰" : "Owner quiet hours"}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {zh
              ? "勾选后，发给业主/客户的短信在其当地时间 21:00–08:00 不立即发送，顺延到当地早上 08:00 由系统补发；内部员工、分包商、供应商照常发送。不勾选则立即发送。"
              : "When checked, SMS to owners/clients is held during their local 21:00–08:00 and delivered at 08:00 local. Internal staff, subs and suppliers are unaffected. Unchecked sends immediately."}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 text-sm text-slate-700">
          <Switch id="comms-quiet-hours-toggle" checked={draft.enabled} disabled={!canEdit || busy}
            onCheckedChange={(next) => setDraft({ ...draft, enabled: !!next })}
            data-testid="comms-quiet-hours-toggle" aria-label={zh ? "业主深夜免打扰" : "Owner quiet hours"} />
          <label htmlFor="comms-quiet-hours-toggle" className="cursor-pointer">
            {zh ? "启用（默认勾选）" : "Enabled (default)"}
          </label>
        </span>
        <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
          {zh ? "安静时段" : "Quiet window"}
          {/* 小时用原生 select：本页密钥输入也是原生控件，且原生下拉在键盘 / 读屏下最稳，
              不需要为两个整点数字再引入一套弹层组件。 */}
          <select value={draft.startHour} onChange={(e) => setDraft({ ...draft, startHour: Number(e.target.value) })}
            disabled={!canEdit || busy} data-testid="comms-quiet-hours-start" aria-label={zh ? "开始小时" : "Start hour"}
            className={`h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 ${FOCUS}`}>
            {ALL_HOURS.map((h) => <option key={h} value={h}>{hours(h)}</option>)}
          </select>
          <span aria-hidden="true">–</span>
          <select value={draft.endHour} onChange={(e) => setDraft({ ...draft, endHour: Number(e.target.value) })}
            disabled={!canEdit || busy} data-testid="comms-quiet-hours-end" aria-label={zh ? "结束小时" : "End hour"}
            className={`h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 ${FOCUS}`}>
            {ALL_HOURS.map((h) => <option key={h} value={h}>{hours(h)}</option>)}
          </select>
        </span>
        {canEdit && (
          <Button size="sm" onClick={save} disabled={busy || !dirty} data-testid="comms-quiet-hours-save"
            className={`bg-indigo-600 text-white ${FOCUS}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            {zh ? "保存" : "Save"}
          </Button>
        )}
        <span data-testid="comms-quiet-hours-status"
          className={`text-xs ${quiet.active ? "text-emerald-700" : "text-amber-700"}`}>
          {status}
        </span>
      </div>
      {!canEdit && !quiet.lockedByEnv && (
        <p className="mt-2 text-xs text-amber-600">{zh ? "只读：仅管理员 / CEO 可修改" : "Read-only: admin / CEO only"}</p>
      )}
    </div>
  );
}
